import Html from './html';
import Video from './video';
import AppOptions from '../app/options';

interface PlaySlideOptions {
    mute?: boolean;
}

class VerticalVideo {

    private p: number = 0;
    private isEnd: boolean = false;
    private isFetching: boolean = false;
    private route: string;
    private hasVerticalPreroll: boolean;
    private videoPlayCount: number = 0;

    constructor(
        private html: Html,
        private video: Video,
        options: AppOptions,
    ) {
        this.route = options.routes?.vertical_videos || '/kratek-video';
        this.hasVerticalPreroll = Boolean(options.ads?.verticalPrerollUrl);
    }

    playSlide(slide: HTMLElement, options: PlaySlideOptions = {}): void {
        const wrapper = this.html.q('[data-video]', slide);
        if (!wrapper) {
            return;
        }

        const videoData = JSON.parse(this.html.getData(wrapper, 'video'));
        const videoDiv = `#video-vertical-player-${videoData.Id}`;

        this.setAdUi(slide, false);
        const playbackOptions = {
            mute: !!options.mute,
            autoplay: true,
            focus: false,
            loop: true,
            replaceUrl: true,
            showPoster: false,
            showVerticalVideoAd: this.shouldPlayPreroll(),
            isVerticalVideo: true,
            onAdStarted: () => this.setAdUi(slide, true),
            onAdCompleted: () => this.setAdUi(slide, false)
        };

        this.video.destroyPlayers();

        this.video
            .play(videoDiv, videoData, playbackOptions)
            .catch(result => this.handlePlaybackInterruption(result))
            .catch(result => {
                console.log('Vertical videos', result?.err);

                // If user opened this video from shared url, than he has probably has
                // not interacted with webpage before and player.play() will not succeed.
                // It will throw error and we'll end up here in catch(). We'll try to
                // play video again, but this time with MUTED volume. Browsers normally
                // allow playing muted videos without user interaction.
                result?.player?.dispose();
                playbackOptions.mute = true;
                return this.video.play(videoDiv, videoData, playbackOptions)
                    .catch(result => this.handlePlaybackInterruption(result));
            })
            .catch(result => {
                // If even mute did not help, let show poster image.
                this.showPlayerPoster(result?.player, videoData);
            });
    }

    private handlePlaybackInterruption(result: any): any {
        if (result?.player?.isDisposed()) {
            return;
        }
        if (result?.err?.name === 'AbortError' && result?.player?.ads?.isInAdMode?.()) {
            return result.player;
        }
        throw result;
    }

    private setAdUi(slide: HTMLElement, isAdPlaying: boolean): void {
        this.html.toggleClassIf(this.html.qAll('.video-ad-label', slide), !isAdPlaying, 'hidden');
    }

    private shouldPlayPreroll(): boolean {
        this.videoPlayCount += 1;

        return this.hasVerticalPreroll
            && this.videoPlayCount >= 3
            && (this.videoPlayCount - 3) % 5 === 0;
    }

    private showPlayerPoster(player: any, videoData: any): void {
        if (!player || !player.poster || !videoData || !videoData.Image) {
            return;
        }

        player.poster(videoData.Image.Src.replace('PLACEHOLDER', '1100xX'));

        const div = this.html.q('.video-js');
        if (div) {
            div.style.setProperty('opacity', '1', 'important');
        }

        player.load();
    }

    handleArrows(index: number, allSlides: number): void {
        if (index > 0) {
            this.html.removeClass('.scroll-up-arrow', 'button-disabled');
        } else {
            this.html.addClass('.scroll-up-arrow', 'button-disabled');
        }

        if (index < allSlides - 1) {
            this.html.removeClass('.scroll-down-arrow', 'button-disabled');
        } else {
            this.html.addClass('.scroll-down-arrow', 'button-disabled');
        }
    }

     loadMoreSlides(excludeIds: number[]): Promise<Array<Element> | null> {
        if (this.isEnd || this.isFetching) {
            return Promise.resolve(null);
        }

        this.p += 1;
        this.isFetching = true;

        const url = this.route + '/?p=' + this.p + '&excludeIds=' + excludeIds.join(',');

        return this.html.fetchText(url)
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                const newSlides = doc.querySelectorAll('.splide__slide');

                if (newSlides.length < 10) {
                    this.isEnd = true;
                }

                this.isFetching = false;

                return Array.from(newSlides);
            })
            .catch(e => {
                console.log('Error loading data from server', e);
                this.isFetching = false;
                return null;
            });
    }

}

export default VerticalVideo;
